import * as React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Skeleton, Stack, Typography } from "@mui/material";
import { CaretDownIcon } from "@phosphor-icons/react/dist/ssr/CaretDown";

import type { Quote } from "@/hooks/api/use-campaign-requests";

import { QuoteLinesTable } from "../components";
import { formatDateTime, formatMoney, formatMoneyRange, quoteAssumptions } from "../constants";

/**
 * Every price this request has ever been offered.
 *
 * A superseded quote is kept and never edited, so the client can always answer "what changed
 * between version 2 and version 3" themselves. Each version carries the assumptions it was priced
 * from, which is what stops the "why is this more than the quote" conversation before it starts —
 * they can see exactly which assumption moved.
 */

export interface QuoteHistoryProps {
	quotes: Quote[];
	/** The live quote. Everything else in the list is history and is drawn as such. */
	currentQuoteId?: string | null;
	isLoading?: boolean;
}

/**
 * A pill only where it says something the row does not.
 *
 * "Superseded" was on every row of a list titled "Earlier versions", which is the same fact
 * written twice — the replaced date beside it is the part worth reading.
 */
function statusChip(quote: Quote, isCurrent: boolean): React.JSX.Element | null {
	if (quote.confirmedAt) return <Chip color="success" label="Confirmed" size="small" variant="soft" />;
	if (isCurrent) return <Chip color="primary" label="Current" size="small" variant="soft" />;
	return null;
}

export function QuoteHistory({ quotes, currentQuoteId, isLoading = false }: QuoteHistoryProps): React.JSX.Element {
	const ordered = React.useMemo(() => [...quotes].toSorted((left, right) => right.version - left.version), [quotes]);

	if (isLoading) return <Skeleton height={120} variant="rounded" />;

	if (ordered.length === 0) {
		return (
			<Typography variant="body2" color="text.secondary">
				Nothing priced yet.
			</Typography>
		);
	}

	return (
		<Stack spacing={1}>
			{ordered.map((quote) => {
				const isCurrent = currentQuoteId ? quote.id === currentQuoteId : !quote.supersededAt;
				const assumptions = quoteAssumptions(quote.assumptions);

				return (
					<Accordion key={quote.id} defaultExpanded={isCurrent} disableGutters variant="outlined">
						<AccordionSummary expandIcon={<CaretDownIcon />}>
							<Stack
								direction={{ xs: "column", sm: "row" }}
								spacing={1.5}
								sx={{ alignItems: { sm: "center" }, flex: "1 1 auto", pr: 2 }}
							>
								<Typography variant="subtitle2" sx={{ minWidth: 90 }}>
									Version {quote.version}
								</Typography>

								{statusChip(quote, isCurrent)}

								<Typography variant="caption" color="text.secondary" sx={{ flex: "1 1 auto" }}>
									Issued {formatDateTime(quote.createdAt)}
									{quote.supersededAt ? ` · replaced ${formatDateTime(quote.supersededAt)}` : ""}
									{quote.confirmedAt ? ` · confirmed ${formatDateTime(quote.confirmedAt)}` : ""}
								</Typography>

								<Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
									{formatMoney(quote.fixedTotal, quote.currency)} fixed ·{" "}
									{formatMoneyRange(quote.variableLow, quote.variableHigh, quote.currency)} estimated
								</Typography>
							</Stack>
						</AccordionSummary>

						<AccordionDetails>
							<Stack spacing={2}>
								<QuoteLinesTable
									lines={quote.lines}
									currency={quote.currency}
									fixedTotal={quote.fixedTotal}
									variableLow={quote.variableLow}
									variableHigh={quote.variableHigh}
								/>

								{assumptions.length > 0 ? (
									<Box>
										<Typography variant="subtitle2" gutterBottom>
											What the Estimate Assumes
										</Typography>
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
											{assumptions.map((assumption) => (
												<Box key={assumption.key} sx={{ minWidth: 160 }}>
													<Typography variant="caption" color="text.secondary">
														{assumption.label}
													</Typography>
													<Typography variant="body2">{assumption.value}</Typography>
												</Box>
											))}
										</Box>
									</Box>
								) : null}
							</Stack>
						</AccordionDetails>
					</Accordion>
				);
			})}
		</Stack>
	);
}
